/**
 * Content — the app's Privacy, Terms, and anything added later.
 *
 * Three columns on purpose: what exists, what you are writing, and what the
 * customer will see. The phone panel is not decoration — these pages are read
 * almost entirely on a 390pt screen, and a paragraph that reads fine in a wide
 * text box can arrive as a wall. Seeing the wrap while writing is the whole
 * point of editing here rather than in a text file.
 *
 * Nothing is live until Publish. Save keeps a draft the app never sees, so an
 * unfinished sentence in a legal document cannot reach a customer by accident.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, IconButton, InputBase, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Toast } from 'primereact/toast';

import * as cms from '../../services/cmsService';

const PINK = '#ED1E79';
const INK = '#1B1420';
const MUTED = '#6E6270';
const LINE = '#E9DEE5';
const GROUND = '#FBF7F9';

/** An empty section, ready to type into. */
const blankSection = () => ({ id: null, title: '', paragraphs: [''], bullets: [] });

/** Slugs are blob paths on the server, which rejects anything else. */
const slugify = (value) =>
  String(value || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

/* ------------------------------------------------------------ password gate */

function PasswordGate({ onUnlock }) {
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    setError('');
    cms.setPassword(value);
    try {
      // The server is the only thing that can say yes. Asking it now means a
      // wrong password is caught here rather than on the first save, after
      // someone has written three paragraphs.
      await cms.listPages();
      onUnlock();
    } catch (err) {
      cms.clearPassword();
      setError(cms.describeError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '60vh', p: 3 }}>
      <Box
        component="form"
        onSubmit={submit}
        sx={{
          width: 380, maxWidth: '100%', bgcolor: '#fff', border: `1px solid ${LINE}`,
          borderRadius: 3, p: 4, textAlign: 'center',
        }}
      >
        <Box sx={{
          width: 52, height: 52, borderRadius: '50%', bgcolor: '#FDF0F6',
          display: 'grid', placeItems: 'center', mx: 'auto', mb: 2,
        }}>
          <LockOutlinedIcon sx={{ color: PINK }} />
        </Box>
        <Typography sx={{ fontWeight: 700, fontSize: 19, color: INK }}>Content</Typography>
        <Typography sx={{ color: MUTED, fontSize: 13.5, mt: 0.5, mb: 3 }}>
          These pages are what customers read in the app. Enter the content password to edit them.
        </Typography>
        <TextField
          type="password" fullWidth size="small" autoFocus
          label="Content password"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          error={!!error}
          helperText={error || ' '}
        />
        <Button
          type="submit" fullWidth variant="contained" disabled={busy || !value}
          sx={{ mt: 1, bgcolor: PINK, '&:hover': { bgcolor: '#c9176a' }, textTransform: 'none', fontWeight: 600 }}
        >
          {busy ? 'Checking…' : 'Unlock'}
        </Button>
      </Box>
    </Box>
  );
}

/* ------------------------------------------------------------- phone preview */

function PhonePreview({ page }) {
  return (
    <Box sx={{
      width: 300, flex: '0 0 300px', bgcolor: '#FDF0F6', borderRadius: 4,
      border: `1px solid ${LINE}`, p: 2, alignSelf: 'flex-start',
      position: 'sticky', top: 16, maxHeight: 'calc(100vh - 120px)', overflow: 'auto',
    }}>
      <Typography sx={{
        fontSize: 10, letterSpacing: '.1em', textTransform: 'uppercase',
        color: MUTED, fontWeight: 700, mb: 1,
      }}>
        As it appears in the app
      </Typography>
      <Box sx={{ bgcolor: '#fff', borderRadius: 3, p: 2 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 16, color: INK }}>
          {page?.title || 'Untitled page'}
        </Typography>
        {page?.effectiveDate ? (
          <Typography sx={{ fontSize: 11, color: MUTED, mt: 0.25 }}>
            Effective {page.effectiveDate}
          </Typography>
        ) : null}
        {page?.lead ? (
          <Typography sx={{ fontSize: 12.5, color: MUTED, mt: 1, lineHeight: 1.55 }}>
            {page.lead}
          </Typography>
        ) : null}
        {(page?.sections || []).map((s, i) => (
          <Box key={i} sx={{ mt: 2 }}>
            {s.title ? (
              <Typography sx={{ fontWeight: 700, fontSize: 13, color: INK, mb: 0.5 }}>
                {s.title}
              </Typography>
            ) : null}
            {(s.paragraphs || []).filter(Boolean).map((p, j) => (
              <Typography key={j} sx={{ fontSize: 12.5, color: '#3A3140', lineHeight: 1.6, mb: 0.75 }}>
                {p}
              </Typography>
            ))}
            {(s.bullets || []).filter(Boolean).map((b, j) => (
              <Box key={j} sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
                <Box sx={{ color: PINK, lineHeight: 1.6 }}>•</Box>
                <Typography sx={{ fontSize: 12.5, color: '#3A3140', lineHeight: 1.6 }}>{b}</Typography>
              </Box>
            ))}
          </Box>
        ))}
        {!(page?.sections || []).length ? (
          <Typography sx={{ fontSize: 12, color: MUTED, mt: 2, fontStyle: 'italic' }}>
            Nothing here yet. Add a section and it appears as you type.
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}

/* ------------------------------------------------------------------- editor */

function SectionCard({
  section, index, total, onChange, onRemove, onMove, onDuplicate,
  onDragStart, onDragOver, onDrop,
}) {
  const set = (patch) => onChange({ ...section, ...patch });
  const kind = (section.bullets || []).length ? 'bullets' : 'paragraphs';

  const setLine = (field, i, value) => {
    const next = [...(section[field] || [])];
    next[i] = value;
    set({ [field]: next });
  };
  const addLine = (field) => set({ [field]: [...(section[field] || []), ''] });
  const removeLine = (field, i) =>
    set({ [field]: (section[field] || []).filter((_, j) => j !== i) });

  return (
    <Box
      draggable
      onDragStart={(e) => onDragStart(e, index)}
      onDragOver={(e) => onDragOver(e, index)}
      onDrop={(e) => onDrop(e, index)}
      sx={{
        border: `1px solid ${LINE}`, borderRadius: 2.5, p: 2, bgcolor: '#fff',
        '&:hover .sec-tools': { opacity: 1 },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Tooltip title="Drag to reorder">
          <DragIndicatorIcon sx={{ color: '#C9BDC6', cursor: 'grab' }} fontSize="small" />
        </Tooltip>
        <InputBase
          value={section.title}
          onChange={(e) => set({ title: e.target.value })}
          placeholder="Section heading"
          sx={{ flex: 1, fontWeight: 700, fontSize: 14.5, color: INK }}
        />
        <Stack direction="row" className="sec-tools" sx={{ opacity: 0, transition: 'opacity .15s' }}>
          {/* Arrows as well as dragging: a long page is easier to reorder with a
              click than a drag across a scrolling list, and dragging is not
              keyboard reachable. */}
          <Tooltip title="Move up"><span>
            <IconButton size="small" disabled={index === 0} onClick={() => onMove(index, -1)}>
              <ArrowUpwardIcon fontSize="inherit" />
            </IconButton>
          </span></Tooltip>
          <Tooltip title="Move down"><span>
            <IconButton size="small" disabled={index === total - 1} onClick={() => onMove(index, 1)}>
              <ArrowDownwardIcon fontSize="inherit" />
            </IconButton>
          </span></Tooltip>
          <Tooltip title="Duplicate">
            <IconButton size="small" onClick={() => onDuplicate(index)}>
              <ContentCopyIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete section">
            <IconButton size="small" onClick={() => onRemove(index)}>
              <DeleteOutlineIcon fontSize="inherit" sx={{ color: '#C0392B' }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <Stack spacing={1}>
        {(section[kind] || []).map((line, i) => (
          <Stack key={i} direction="row" spacing={1} alignItems="flex-start">
            {kind === 'bullets' ? (
              <Box sx={{ color: PINK, pt: 1.2, fontWeight: 700 }}>•</Box>
            ) : null}
            <TextField
              value={line}
              onChange={(e) => setLine(kind, i, e.target.value)}
              placeholder={kind === 'bullets' ? 'Bullet point' : 'Paragraph'}
              multiline minRows={kind === 'bullets' ? 1 : 2}
              fullWidth size="small"
            />
            <IconButton size="small" onClick={() => removeLine(kind, i)} sx={{ mt: 0.5 }}>
              <DeleteOutlineIcon fontSize="inherit" />
            </IconButton>
          </Stack>
        ))}
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
        <Button size="small" startIcon={<AddIcon />} onClick={() => addLine('paragraphs')}
          sx={{ textTransform: 'none', color: MUTED }}>
          Paragraph
        </Button>
        <Button size="small" startIcon={<AddIcon />} onClick={() => addLine('bullets')}
          sx={{ textTransform: 'none', color: MUTED }}>
          Bullet
        </Button>
      </Stack>
    </Box>
  );
}

export function ContentCMS() {
  const toast = useRef(null);
  const dragFrom = useRef(null);

  const [unlocked, setUnlocked] = useState(!!cms.getPassword());
  const [pages, setPages] = useState([]);
  const [slug, setSlug] = useState(null);
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState('');
  const [dirty, setDirty] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');

  const say = (severity, summary, detail) =>
    toast.current?.show?.({ severity, summary, detail, life: 4000 });

  const refreshList = useCallback(async () => {
    try {
      const list = await cms.listPages();
      setPages(list);
      return list;
    } catch (err) {
      say('error', 'Content', cms.describeError(err));
      return [];
    }
  }, []);

  useEffect(() => {
    if (!unlocked) return;
    (async () => {
      const list = await refreshList();
      if (list.length && !slug) setSlug(list[0].slug);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unlocked]);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setLoading(true);
    cms.getPage(slug)
      .then((data) => {
        if (cancelled) return;
        setPage({ ...data, sections: data.sections?.length ? data.sections : [blankSection()] });
        setDirty(false);
      })
      .catch((err) => !cancelled && say('error', 'Content', cms.describeError(err)))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [slug]);

  const patch = (next) => { setPage(next); setDirty(true); };

  const setSection = (i, section) =>
    patch({ ...page, sections: page.sections.map((s, j) => (j === i ? section : s)) });

  const moveSection = (i, delta) => {
    const to = i + delta;
    if (to < 0 || to >= page.sections.length) return;
    const next = [...page.sections];
    [next[i], next[to]] = [next[to], next[i]];
    patch({ ...page, sections: next });
  };

  const onDragStart = (e, i) => { dragFrom.current = i; e.dataTransfer.effectAllowed = 'move'; };
  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };
  const onDrop = (e, to) => {
    e.preventDefault();
    const from = dragFrom.current;
    dragFrom.current = null;
    if (from == null || from === to) return;
    const next = [...page.sections];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    patch({ ...page, sections: next });
  };

  const save = async () => {
    setBusy('save');
    try {
      const saved = await cms.savePage(slug, page);
      setPage({ ...saved, sections: saved.sections?.length ? saved.sections : [blankSection()] });
      setDirty(false);
      await refreshList();
      say('success', 'Draft saved', 'Customers still see the published version.');
    } catch (err) {
      say('error', 'Could not save', cms.describeError(err));
    } finally { setBusy(''); }
  };

  const publish = async () => {
    setBusy('publish');
    try {
      // Save first, always. Publishing promotes the stored draft, so a publish
      // without a save would push the last saved wording and quietly discard
      // whatever is on screen.
      if (dirty) await cms.savePage(slug, page);
      const live = await cms.publishPage(slug);
      setPage({ ...live, sections: live.sections?.length ? live.sections : [blankSection()] });
      setDirty(false);
      await refreshList();
      say('success', 'Published', 'The app updates without anyone reopening it.');
    } catch (err) {
      say('error', 'Could not publish', cms.describeError(err));
    } finally { setBusy(''); }
  };

  const create = async () => {
    const nextSlug = slugify(newTitle);
    if (!nextSlug) return;
    setBusy('create');
    try {
      await cms.createPage({ slug: nextSlug, title: newTitle.trim(), sections: [] });
      setNewOpen(false); setNewTitle('');
      await refreshList();
      setSlug(nextSlug);
      say('success', 'Page created', 'It is a draft until you publish it.');
    } catch (err) {
      say('error', 'Could not create', cms.describeError(err));
    } finally { setBusy(''); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${page?.title || slug}"? This removes it from the app.`)) return;
    setBusy('delete');
    try {
      await cms.deletePage(slug);
      const list = await refreshList();
      setSlug(list[0]?.slug || null);
      setPage(null);
      say('success', 'Deleted', 'It has gone from the app too.');
    } catch (err) {
      say('error', 'Could not delete', cms.describeError(err));
    } finally { setBusy(''); }
  };

  const current = useMemo(() => pages.find((p) => p.slug === slug), [pages, slug]);

  if (!unlocked) return <PasswordGate onUnlock={() => setUnlocked(true)} />;

  return (
    <Box sx={{ p: 2.5, bgcolor: GROUND, minHeight: '100%' }}>
      <Toast ref={toast} />
      <Stack direction="row" spacing={2.5} alignItems="flex-start">

        {/* ---------------------------------------------------------- pages */}
        <Box sx={{
          width: 218, flex: '0 0 218px', bgcolor: '#fff', border: `1px solid ${LINE}`,
          borderRadius: 3, p: 1.5, position: 'sticky', top: 16,
        }}>
          <Typography sx={{
            fontSize: 10, letterSpacing: '.12em', textTransform: 'uppercase',
            color: MUTED, fontWeight: 700, px: 1, pb: 1,
          }}>
            Pages
          </Typography>
          <Stack spacing={0.5}>
            {pages.map((p) => {
              const active = p.slug === slug;
              return (
                <Box key={p.slug} onClick={() => setSlug(p.slug)} sx={{
                  px: 1.25, py: 1, borderRadius: 2, cursor: 'pointer', fontSize: 13,
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1,
                  bgcolor: active ? PINK : 'transparent',
                  color: active ? '#fff' : '#3A3140',
                  fontWeight: active ? 600 : 400,
                  '&:hover': { bgcolor: active ? PINK : '#F7F2F5' },
                }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {p.slug.replace(/-/g, ' ')}
                  </span>
                  <Tooltip title={p.hasDraft ? 'Unpublished changes' : 'Published'}>
                    <Box sx={{
                      width: 7, height: 7, borderRadius: '50%', flex: 'none',
                      bgcolor: p.hasDraft ? '#E9A23B' : '#1F7A4D',
                    }} />
                  </Tooltip>
                </Box>
              );
            })}
          </Stack>
          <Button
            fullWidth startIcon={<AddIcon />} onClick={() => setNewOpen(true)}
            sx={{
              mt: 1.5, textTransform: 'none', fontWeight: 600, color: PINK,
              border: `1px dashed ${PINK}`, borderRadius: 2,
            }}
          >
            New page
          </Button>
          <Divider sx={{ my: 1.5 }} />
          <Stack spacing={0.75} sx={{ px: 1 }}>
            {[['#1F7A4D', 'Published'], ['#E9A23B', 'Unpublished changes']].map(([c, label]) => (
              <Stack key={label} direction="row" spacing={1} alignItems="center">
                <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: c }} />
                <Typography sx={{ fontSize: 11, color: MUTED }}>{label}</Typography>
              </Stack>
            ))}
          </Stack>
        </Box>

        {/* --------------------------------------------------------- editor */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {loading ? (
            <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}><CircularProgress /></Box>
          ) : !page ? (
            <Box sx={{
              bgcolor: '#fff', border: `1px solid ${LINE}`, borderRadius: 3, p: 6,
              textAlign: 'center', color: MUTED,
            }}>
              <Typography sx={{ fontWeight: 700, color: INK, mb: 0.5 }}>No page selected</Typography>
              <Typography sx={{ fontSize: 13.5 }}>
                Pick one on the left, or create the first page.
              </Typography>
            </Box>
          ) : (
            <Stack spacing={2}>
              <Box sx={{ bgcolor: '#fff', border: `1px solid ${LINE}`, borderRadius: 3, p: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start"
                  spacing={2} flexWrap="wrap" useFlexGap>
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <InputBase
                        value={page.title}
                        onChange={(e) => patch({ ...page, title: e.target.value })}
                        placeholder="Page title"
                        sx={{ fontWeight: 700, fontSize: 20, color: INK }}
                      />
                      <Chip
                        size="small"
                        label={current?.hasDraft || dirty ? 'Draft' : 'Live'}
                        sx={{
                          height: 20, fontSize: 10.5, fontWeight: 700, letterSpacing: '.05em',
                          bgcolor: current?.hasDraft || dirty ? '#FBF1E0' : '#EAF5EF',
                          color: current?.hasDraft || dirty ? '#9A6508' : '#1F7A4D',
                        }}
                      />
                    </Stack>
                    <Typography sx={{ fontSize: 11.5, color: MUTED, fontFamily: 'monospace' }}>
                      en/{page.slug}.json
                      {page.updatedAt ? ` · updated ${page.updatedAt.slice(0, 16).replace('T', ' ')}` : ''}
                      {page.updatedBy ? ` by ${page.updatedBy}` : ''}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1}>
                    <Button onClick={remove} disabled={!!busy}
                      sx={{ textTransform: 'none', color: '#C0392B' }}>
                      Delete
                    </Button>
                    <Button onClick={save} disabled={!!busy || !dirty} variant="outlined"
                      sx={{ textTransform: 'none', fontWeight: 600, borderColor: LINE, color: INK }}>
                      {busy === 'save' ? 'Saving…' : 'Save draft'}
                    </Button>
                    <Button onClick={publish} disabled={!!busy} variant="contained"
                      sx={{ textTransform: 'none', fontWeight: 600, bgcolor: PINK,
                        '&:hover': { bgcolor: '#c9176a' } }}>
                      {busy === 'publish' ? 'Publishing…' : 'Publish'}
                    </Button>
                  </Stack>
                </Stack>

                <Stack direction="row" spacing={2} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
                  <TextField
                    label="Effective date" size="small" placeholder="2026-02-17"
                    value={page.effectiveDate || ''}
                    onChange={(e) => patch({ ...page, effectiveDate: e.target.value })}
                    sx={{ width: 190 }}
                  />
                  <TextField
                    label="Intro (optional)" size="small" fullWidth multiline maxRows={3}
                    value={page.lead || ''}
                    onChange={(e) => patch({ ...page, lead: e.target.value })}
                    sx={{ flex: 1, minWidth: 260 }}
                  />
                </Stack>
              </Box>

              {page.sections.map((section, i) => (
                <SectionCard
                  key={i}
                  section={section} index={i} total={page.sections.length}
                  onChange={(s) => setSection(i, s)}
                  onRemove={(idx) => patch({
                    ...page, sections: page.sections.filter((_, j) => j !== idx),
                  })}
                  onDuplicate={(idx) => {
                    const next = [...page.sections];
                    next.splice(idx + 1, 0, JSON.parse(JSON.stringify(page.sections[idx])));
                    patch({ ...page, sections: next });
                  }}
                  onMove={moveSection}
                  onDragStart={onDragStart} onDragOver={onDragOver} onDrop={onDrop}
                />
              ))}

              <Button
                startIcon={<AddIcon />}
                onClick={() => patch({ ...page, sections: [...page.sections, blankSection()] })}
                sx={{
                  textTransform: 'none', color: MUTED, border: `1px dashed ${LINE}`,
                  borderRadius: 2.5, py: 1.25, bgcolor: '#fff',
                }}
              >
                Add section
              </Button>
            </Stack>
          )}
        </Box>

        {/* -------------------------------------------------------- preview */}
        {page ? <PhonePreview page={page} /> : null}
      </Stack>

      <Dialog open={newOpen} onClose={() => setNewOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700 }}>New page</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus fullWidth size="small" label="Page title" sx={{ mt: 1 }}
            value={newTitle} onChange={(e) => setNewTitle(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && create()}
          />
          <Typography sx={{ fontSize: 12, color: MUTED, mt: 1.5, fontFamily: 'monospace' }}>
            {newTitle ? `en/${slugify(newTitle)}.json` : 'en/….json'}
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: MUTED, mt: 1 }}>
            It starts as a draft. Nothing appears in the app until you publish it.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setNewOpen(false)} sx={{ textTransform: 'none', color: MUTED }}>
            Cancel
          </Button>
          <Button onClick={create} disabled={!slugify(newTitle) || busy === 'create'}
            variant="contained"
            sx={{ textTransform: 'none', fontWeight: 600, bgcolor: PINK,
              '&:hover': { bgcolor: '#c9176a' } }}>
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ContentCMS;
